import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
    DeleteRecord,
    GetListview,
    SuccessAlert,
    URLPRIFIX,
    ErrorAlert,
    UpdateRecord,
    GetRecords,
    GetStatusCounts,
} from "../../functions/api";
import { useStore } from "../../Components/StateProvider";
import Pagination from "../../Components/Fields/Pagination";
import { getlocal } from "../../functions/localstorage";
import BulkActionsBar from "../../Components/Elements/BulkActionsBar";
import BulkUpdateModal from "../../Components/Elements/BulkUpdateModal";
import StatusTabs from "../../Components/Elements/StatusTabs";
import ListViewUp from "../../Components/Elements/ListViewUp";

const DocumentsListUpdate = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const store = useStore();
    const currentUser = getlocal("system")?.user || {};
    const tablename = "documents";
    const tableURL = "/tasks/getprojectstasklist/11";
    const tableURL_USER = `/tasks/getprojectstasklist/11/${currentUser.id}`;
    const statusCountURL = "/tasks/getprojecttaskstatuscount/11";
    const DeleteURL = "/tasks/deletetask/";

    const [users, setUsers] = useState([]);
    const [statusTabs, setStatusTabs] = useState([]);
    const [activeTab, setActiveTab] = useState("ALL");
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [hasTabClicked, setHasTabClicked] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filters, setFilters] = useState({});
    const [sortConfig, setSortConfig] = useState({ field: "", direction: "asc" });

    const Tablehead = "pr-[10px] max-sm:text-[26px] md:text-[20px] lg:text-[16px] xl:text-[20px] font-bold";
    const Tablerecord = "pr-[10px] max-sm:text-[26px] md:text-[20px] lg:text-[12px] xl:text-[16px] overflow-hidden";

    const header = [
        ["Document", "task_title"],
        ["Project", "projectName"],
        ["Client", "clientname"],
        ["Assigned", "assigned_to_username"],
        ["Status", "status"],
        ["Due Date", "dueDate"],
        ["Created Date", "createdDate"],
        ["Action", "ACTION"],
    ];

    const init = {
        filterfield: "all",
        search: "",
        sort: "asc",
        currentpage: 1,
        totalpage: 1,
        pagesize: 10,
    };

    const fetchStatusCounts = useCallback(async () => {
        store.startLoading();
        try {
            const counts = await GetStatusCounts(statusCountURL, store);
            if (counts?.statuscount) {
                setStatusTabs(counts.statuscount);
            }
        } catch {
            ErrorAlert("Failed to fetch status counts.");
        } finally {
            store.stopLoading();
        }
    }, []);

    const fetchUsers = useCallback(async () => {
        try {
            const response = await GetRecords("/users/getusers?fieldname=all&sort=asc&page=1&count=10&pagesize=1000");
            if (response?.record?.list?.length) {
                setUsers(response.record.list);
            }
        } catch { }
    }, []);

    const loadListview = useCallback((params = {}) => {
        GetListview({
            listUrl: currentUser.role === 1 ? tableURL : tableURL_USER,
            store,
            field: params.field ?? store.getvalue("documentFilterField") ?? init.filterfield,
            search: params.search ?? store.getvalue("documentSearch") ?? init.search,
            sort: params.sort ?? store.getvalue("documentSort") ?? init.sort,
            page: params.page ?? store.getvalue("documentCurrentPage") ?? init.currentpage,
            pagesize: params.pagesize ?? store.getvalue("documentPageSize") ?? init.pagesize,
        });
    }, []);

    useEffect(() => {
        store.setmodule("nonlitigation_documents", init).then(() => {
            store.startLoading();
            store.setheader(`${tablename} List`);
            store.setvalue("defaultpagesize", init.pagesize);
            fetchStatusCounts();
            const states = store?.state?.records?.nonlitigation_documents;
            const storedFilters = store.getvalue("documentFilters");
            if (storedFilters) {
                setFilters(storedFilters);
                setHasTabClicked(true);
            }
            loadListview({
                field: queryParams.get("field") || states?.documentFilterField,
                search: queryParams.get("search") || states?.documentSearch,
                sort: queryParams.get("sort") || states?.documentSort,
                pagesize: queryParams.get("documentPageSize") || states?.documentPageSize,
            });
        });
    }, []);

    const change = useCallback((props) => {
        const updatedFilters = props.field ? { ...filters, [props.field]: props.search } : {};
        setFilters(updatedFilters);
        store.setvalue("documentFilters", updatedFilters);
        store.setvalue("documentFilterField", props.field || "all");
        store.setvalue("documentSearch", props.search || "");
        store.setvalue("documentSort", props.sort || init.sort);
        store.setvalue("documentPageSize", props.pagesize || store.getvalue("defaultpagesize"));
        store.setvalue("documentCurrentPage", 1);
        loadListview({ ...props, page: 1 });
    }, [filters]);

    const onPageChange = useCallback((page, pagesize = store.getvalue("documentPageSize")) => {
        store.setvalue("documentCurrentPage", page);
        loadListview({
            field: store.getvalue("documentFilterField"),
            search: store.getvalue("documentSearch"),
            sort: store.getvalue("documentSort"),
            page,
            pagesize,
        });
    }, []);
    const records = store.getvalue("list") || [];

    const filteredRecords = records
        .filter((record) => {
            const upper = activeTab.toUpperCase();
            if (
                upper !== "ALL" &&
                upper !== "ASSIGNED" &&
                upper !== "UNASSIGNED" &&
                record.status?.toUpperCase() !== upper
            ) return false;
            if (upper === "ASSIGNED" && !record.assigned_to) return false;
            if (upper === "UNASSIGNED" && record.assigned_to) return false;
            return header.every(([_, label]) => {
                const filterVal = filters[label];
                if (!filterVal) return true;
                let fieldVal = record[label];
                if (label.toLowerCase().includes("date") && fieldVal) {
                    const parsed = new Date(fieldVal);
                    fieldVal = !isNaN(parsed) ? parsed.toISOString().split("T")[0] : fieldVal;
                }
                return (
                    fieldVal &&
                    fieldVal.toString().toLowerCase().includes(filterVal.toLowerCase())
                );
            });
        })
        .sort((a, b) => {
            const { field, direction } = sortConfig;
            if (!field) return 0;
            const aVal = (a[field] ?? "").toString().toLowerCase();
            const bVal = (b[field] ?? "").toString().toLowerCase();
            return direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });

    const handleOnSelect = useCallback((items, isChecked) => {
        if (Array.isArray(items)) {
            setSelectedRecords(isChecked ? [...items] : []);
        } else {
            setSelectedRecords((prev) =>
                isChecked ? [...prev, items] : prev.filter((r) => r.id !== items.id)
            );
        }
    }, []);

    const handleBulkAssign = useCallback(() => {
        setIsModalOpen(true);
        fetchUsers();
    }, []);

    const handleBulkAssignUpdate = useCallback(async (assignedToId) => {
        const dueDateIso = assignedToId.dueDate ? new Date(assignedToId.dueDate).toISOString() : null;
        store.startLoading();
        try {
            const taskIds = selectedRecords.map((item) => item.id);
            const payload = {
                task_ids: taskIds,
                assigned_to: assignedToId.assignedTo,
                modified_by: store.getuser().id,
                ...(assignedToId.dueDate && { due_date: dueDateIso }),
            };

            const res = await UpdateRecord("/tasks/bulkupdatedocuments/11", payload);
            if (res?.success) {
                SuccessAlert("Selected items assigned successfully!");
            } else {
                ErrorAlert(res?.errorMessage || "Bulk update failed.");
            }
        } catch (error) {
            ErrorAlert(`Bulk update failed: ${error.message}`);
        } finally {
            setIsModalOpen(false);
            setSelectedRecords([]);
            loadListview();
            store.stopLoading();
        }
    }, [selectedRecords]);

    const handleBulkDelete = useCallback(async (itemsToDelete) => {
        if (!window.confirm(`Are you sure you want to delete ${itemsToDelete.length} selected items?`)) return;
        store.startLoading();
        let allSuccess = true;
        for (const item of itemsToDelete) {
            try {
                const res = await DeleteRecord(`${DeleteURL}${item.id}`, {
                    modifiedBy: store.getuser().id,
                });
                if (!res.success) {
                    allSuccess = false;
                    ErrorAlert(`Error deleting item ${item.id}`);
                }
            } catch (error) {
                allSuccess = false;
                ErrorAlert(`Error deleting item ${item.id}: ${error.message}`);
            }
        }
        allSuccess ? SuccessAlert("Selected items deleted successfully!") : ErrorAlert("Some items failed.");
        setSelectedRecords([]);
        loadListview();
        fetchStatusCounts();
        store.stopLoading();
    }, [selectedRecords]);

    const handleDelete = useCallback(async (item) => {
        if (!window.confirm("Are you sure you want to delete?")) return;
        store.startLoading();
        try {
            const res = await DeleteRecord(`${DeleteURL}${item.id}`, {
                modifiedBy: store.getuser().id,
            });
            if (res.success) {
                SuccessAlert("Deleted Successfully");
                setSelectedRecords((prev) => prev.filter((r) => r.id !== item.id));
                loadListview();
                fetchStatusCounts();
            } else {
                ErrorAlert("Error Found. Please contact the Admin.");
            }
        } catch (error) {
            ErrorAlert(`Error deleting item: ${error.message}`);
        } finally {
            store.stopLoading();
        }
    }, []);

    const handleClickRow = useCallback((item) => {
        if (store.getprops("view")) {
            store.navTo(`/${URLPRIFIX}/${tablename}/view/${item.id}`);
        }
    }, []);

    const handleSort = useCallback((label) => {
        const direction = sortConfig.field === label && sortConfig.direction === "asc" ? "desc" : "asc";
        setSortConfig({ field: label, direction });
        change({
            field: store.getvalue("documentFilterField"),
            search: store.getvalue("documentSearch"),
            sort: direction,
            pagesize: store.getvalue("documentPageSize"),
        });
    }, [sortConfig, change]);

    const handleFilterChange = useCallback((label, value) => {
        const updatedFilters = { ...filters, [label]: value };
        setFilters(updatedFilters);
        change({
            field: label,
            search: value,
            pagesize: store.getvalue("documentPageSize"),
            sort: sortConfig.direction,
        });
    }, [filters, sortConfig]);

    const handleSelectAll = useCallback((e) => {
        handleOnSelect(e.target.checked ? filteredRecords : [], e.target.checked);
    }, [filteredRecords]);

    const isSelected = useCallback((item) => selectedRecords.some((s) => s.id === item.id), [selectedRecords]);

    const isAllSelected =
        filteredRecords.length > 0 &&
        selectedRecords.length === filteredRecords.length &&
        filteredRecords.every((r) => selectedRecords.some((s) => s.id === r.id));

    const handleTabClick = useCallback((status, count) => {
        const upper = status.toUpperCase();
        setActiveTab(status);
        setHasTabClicked(true);
        store.setvalue("documentActiveTab", status);
        store.setvalue("documentTabClicked", true);
        const pageSize = count;
        store.setvalue("documentPageSize", pageSize);
        const field = upper === "ASSIGNED" || upper === "UNASSIGNED" ? "assigned_to" : "status";
        const search =
            upper === "ALL" ? "" : upper === "ASSIGNED" ? "gt:0" : upper === "UNASSIGNED" ? "eq:0" : upper;
        change({ field, search, pagesize: pageSize, sort: store.getvalue("documentSort") });
        const filterValue = upper === "ALL" ? {} : { [field]: search };
        setFilters(filterValue);
        store.setvalue("documentFilters", filterValue);
    }, []);

    const handleClearFilter = useCallback(() => {
        setActiveTab("ALL");
        setFilters({});
        setHasTabClicked(false);
        store.setvalue("documentFilters", {});
        store.setvalue("documentPageSize", init.pagesize);
        store.setvalue("documentFilterField", init.filterfield);
        store.setvalue("documentSearch", init.search);
        store.setvalue("documentSort", init.sort);
        store.setvalue("documentCurrentPage", init.currentpage);
        store.setvalue("documentActiveTab", "ALL");
        store.setvalue("documentTabClicked", false);
        loadListview({ ...init });
    }, []);



    return (
        <div className="grid grid-rows-10 h-full">
            <div className="flex flex-wrap justify-between items-center mb-2 px-2">
                <div className="flex-1 overflow-x-auto">
                    <StatusTabs
                        statusTabs={statusTabs}
                        activeTab={activeTab}
                        onTabClick={handleTabClick}
                        hasTabClicked={hasTabClicked}
                        onClearClick={handleClearFilter}
                    />
                </div>
                {store.getprops("add") && (
                    <button
                        className="bg-primary text-secondary px-3 py-1 rounded-full border-2 transition text-sm whitespace-nowrap"
                        onClick={() => {
                            store.removemodule("nonlitigation_document");
                            store.navTo(`/demo/${tablename}/create`);
                        }}
                    >
                        ➕ ADD NEW
                    </button>
                )}
            </div>

            <div className="row-span-9 h-full w-full">
                <div className="py-5 px-[30px] h-full w-full">
                    <ListViewUp
                        header={header}
                        filteredRecords={filteredRecords}
                        Tablehead={Tablehead}
                        Tablerecord={Tablerecord}
                        sortConfig={sortConfig}
                        handleSort={handleSort}
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        store={store}
                        handleOnSelect={handleOnSelect}
                        isSelected={isSelected}
                        handleClickRow={handleClickRow}
                        handleDelete={handleDelete}
                        handleSelectAll={handleSelectAll}
                        isAllSelected={isAllSelected}
                        tablename={tablename}
                    />
                    <BulkActionsBar
                        selectedRecords={selectedRecords}
                        store={store}
                        onBulkAssign={handleBulkAssign}
                        onBulkDelete={() => handleBulkDelete(selectedRecords)}
                    />
                    <BulkUpdateModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onUpdate={handleBulkAssignUpdate}
                        users={users}
                    />
                    <div className="float-right mr-8 mt-6">
                        <Pagination onchange={onPageChange} />
                    </div>
                </div>
            </div>
        </div>
        // </div>
    );
};

export default DocumentsListUpdate;
